/* eslint-disable max-lines-per-function */
import React, { useState } from "react";

import { gql } from "graphql-tag";
import { useMutation, useQuery } from "@apollo/client";
import { Column, Options } from "@material-table/core";
import { RouteComponentProps } from "@reach/router";
import { EditableTable } from "components/haven-design/editable-table";
import { LoadingSpinner } from "components/graphql-views";
import { createFormQuestionType, ValueType, createFormQuestionTypeVariables, updateFormQuestionTypeVariables, updateFormQuestionType, getFormQuestions } from "types/gql-types";
import { Button } from "components/haven-design";
import FormNamesModal from "./form-names-modal";


type FormQuestionType = {
  id?: string;
  name: string;
  valueType: ValueType;
  usedBy: number;
}

const tableOptions: Options<FormQuestionType> = {
  pageSize: 10,
};

export const GET_FORM_QUESTIONS = gql`
  query getFormQuestions {
    formQuestions {
      id
      name
      valueType
      usedBy
    }
  }
`;

const CREATE_FORM_QUESTION = gql`
  mutation createFormQuestionType($data: FormQuestionInput!) {
    createFormQuestion(data: $data) {
      name
      valueType
    }
  }
`;

const UPDATE_QUESTION = gql`
  mutation updateFormQuestionType($id: ID!, $data: FormQuestionInput!) {
    updateFormQuestion(id: $id, data: $data) {
      id
      name
      valueType
    }
  }
`;

const FormQuestions: React.FC<RouteComponentProps> = () => {
  const { loading, error, data: response, refetch } =
    useQuery<getFormQuestions>(GET_FORM_QUESTIONS, { fetchPolicy: "no-cache" });
  const [createNewQuestion] = useMutation<
  createFormQuestionType,
  createFormQuestionTypeVariables
  >(CREATE_FORM_QUESTION);
  const [updateQuestion] = useMutation<
  updateFormQuestionType,
  updateFormQuestionTypeVariables
  >(UPDATE_QUESTION);

  const [open, setOpen] = useState<boolean>(false);
  const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(null);
  const [questionName, setQuestionName] = useState<string | null>(null);

  const handleClose = () => setOpen(false);

  const handleOpen = (questionId: string, currentQuestionName: string) => {
    setCurrentQuestionId(questionId);
    setQuestionName(currentQuestionName);
    setOpen(true);
  };

  if (error) return <span>Server Error</span>;
  if (loading) return <LoadingSpinner />;
  if (!response) return null;

  const editable = {
    onRowAdd: (data: FormQuestionType) => new Promise((resolve) => {
      createNewQuestion({ variables: { data } })
        .then(() => {
          resolve(true);
          refetch();
        });
    }),
    onRowUpdate: ({ name, valueType }: FormQuestionType, oldData: { id: string; }) => (
      new Promise((resolve) => {
        updateQuestion({ variables: { id: oldData.id, data: { name, valueType } } })
          .then(() => {
            resolve(true);
            refetch();
          });
      })),
  };

  const columns: Array<Column<FormQuestionType>> = [
    { title: "Name", field: "name" },
    { title: "ValueType",
      field: "valueType",
      type: "string",
      initialEditValue: "text",
      lookup: Object.entries(ValueType)
        .reduce((acc, [key, value]: [string, ValueType]) => ({
          ...acc, [key]: value,
        }), {}) },
    { title: "Used By",
      field: "usedBy",
      render: ({ id, usedBy, name }) => (id && usedBy > 0 ? (
        <Button
          color="secondary"
          onClick={() => handleOpen(id, name)}
        >
          {usedBy}
        </Button>) : usedBy) },
  ];

  return (
    <>
      <EditableTable
        editable={editable}
        columns={columns}
        data={response.formQuestions as FormQuestionType[]}
        title="Form Questions"
        options={tableOptions}
        style={{ padding: "1.5em" }}
      />
      <FormNamesModal
        open={open} questionId={currentQuestionId}
        questionName={questionName} onClose={handleClose}
      />
    </>
  );
};

export default FormQuestions;
